import React, { FC } from 'react';
import styled from 'styled-components';

interface WizardStepIndicatorProps {
    /** Ordered step labels, rendered under each dot. Length defines the number of dots. */
    steps: string[];
    /** 0-based index of the active step. */
    currentStep: number;
    /** Per-step completion flags, aligned with `steps`. A completed step shows a check instead of its number. */
    completedSteps: boolean[];
}

const StepIndicator = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: center;
    gap: 8px;
    margin-bottom: 32px;
`;

const StepItem = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 32px;
    flex: 0 0 auto;
`;

const StepDot = styled.div<{ $active: boolean; $completed: boolean }>`
    width: 32px;
    height: 32px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;
    font-weight: 600;
    flex: 0 0 auto;
    color: ${(props) => (props.$active || props.$completed ? '#fff' : '#9e9e9e')};
    background: ${(props) => (props.$completed ? '#2e7d32' : props.$active ? props.theme.app.primary : '#e0e0e0')};
    transition: all 0.2s ease;
`;

const StepLabel = styled.span<{ $active: boolean; $completed: boolean }>`
    margin-top: 6px;
    font-size: 11px;
    line-height: 1.3;
    text-align: center;
    white-space: nowrap;
    font-weight: ${(props) => (props.$active ? 600 : 400)};
    color: ${(props) => (props.$active ? props.theme.app.primary : props.$completed ? '#2e7d32' : '#9e9e9e')};
`;

const StepLine = styled.div<{ $completed: boolean }>`
    width: 40px;
    height: 2px;
    margin-top: 15px;
    flex: 0 0 auto;
    background: ${(props) => (props.$completed ? '#2e7d32' : '#e0e0e0')};
    transition: all 0.2s ease;
`;

/**
 * Numbered step indicator (dots joined by connectors, green check when complete) shared across the app's step-based
 * wizards. Mirrors the visual language of the Print-on-Demand integration wizards; labels under each dot make it
 * readable across a multi-stage journey.
 */
export const WizardStepIndicator: FC<WizardStepIndicatorProps> = ({ steps, currentStep, completedSteps }) => (
    <StepIndicator>
        {steps.map((label, i) => {
            const completed = completedSteps[i] ?? false;
            return (
                <React.Fragment key={i}>
                    {i > 0 && <StepLine $completed={completedSteps[i - 1] ?? false} />}
                    <StepItem>
                        <StepDot $active={currentStep === i} $completed={completed}>
                            {completed ? '✓' : i + 1}
                        </StepDot>
                        <StepLabel $active={currentStep === i} $completed={completed}>
                            {label}
                        </StepLabel>
                    </StepItem>
                </React.Fragment>
            );
        })}
    </StepIndicator>
);

export default WizardStepIndicator;
